import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Send, Flag, Ban } from "lucide-react";
import { api, imgUrl, formatPrice, errText, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BadgeChip } from "../components/BadgeChip";
import { toast } from "sonner";

function OfferCard({ offer, user, onAction }) {
  const isMine = offer.made_by === user.id;
  const canRespond = offer.status === "PENDING" && !isMine;
  const [counter, setCounter] = useState("");
  const [showCounter, setShowCounter] = useState(false);
  const act = async (action, body) => {
    try {
      await api.post(`/offers/${offer.id}/${action}`, body || {});
      toast.success(action === "accept" ? "Angebot angenommen" : action === "reject" ? "Angebot abgelehnt" : "Gegenangebot gesendet");
      onAction();
    } catch (e) { toast.error(errText(e)); }
  };
  const STATUS = { PENDING: "Warten auf Antwort", ACCEPTED: "Angenommen", REJECTED: "Abgelehnt", COUNTER_OFFERED: "Gegenangebot gemacht", EXPIRED: "Abgelaufen", CANCELLED: "Zurückgezogen" };
  return (
    <div data-testid={`offer-card-${offer.id}`} className={`border rounded-2xl p-3.5 ${offer.status === "ACCEPTED" ? "border-accent bg-accent/5" : "border-border bg-secondary/40"}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] font-bold tracking-wide text-muted-foreground uppercase">Angebot {isMine ? "(von dir)" : ""}</p>
          <p className="text-lg font-bold">{formatPrice(offer.offer_price)} <span className="text-xs text-muted-foreground font-normal line-through">{formatPrice(offer.original_price)}</span></p>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${offer.status === "ACCEPTED" ? "bg-accent text-white" : "bg-muted"}`}>{STATUS[offer.status] || offer.status}</span>
      </div>
      {canRespond && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button data-testid={`offer-accept-${offer.id}`} onClick={() => act("accept")} className="bg-foreground text-white text-xs font-bold px-4 py-2 rounded-full hover:opacity-90">Annehmen</button>
          <button data-testid={`offer-reject-${offer.id}`} onClick={() => act("reject")} className="border border-border text-xs font-bold px-4 py-2 rounded-full hover:bg-secondary">Ablehnen</button>
          <button data-testid={`offer-counter-toggle-${offer.id}`} onClick={() => setShowCounter(!showCounter)} className="border-2 border-accent text-accent text-xs font-bold px-4 py-2 rounded-full hover:bg-accent/10">Gegenangebot</button>
        </div>
      )}
      {showCounter && canRespond && (
        <div className="flex gap-2 mt-2">
          <input data-testid={`offer-counter-input-${offer.id}`} type="number" inputMode="decimal" step="0.01" placeholder="Dein Preis €" value={counter} onChange={(e) => setCounter(e.target.value)}
            className="flex-1 rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <button data-testid={`offer-counter-send-${offer.id}`} disabled={!parseFloat(counter)} onClick={() => act("counter", { offer_price: parseFloat(counter) })}
            className="bg-accent text-white text-xs font-bold px-4 rounded-xl disabled:opacity-40">Senden</button>
        </div>
      )}
      {offer.status === "ACCEPTED" && user.id === offer.buyer_id && (
        <a href={`/kaufen/${offer.item_id}`} data-testid={`offer-buy-now-${offer.id}`} className="block mt-3 bg-accent text-white text-center text-sm font-bold py-2.5 rounded-full hover:opacity-90">Jetzt kaufen</a>
      )}
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const [convs, setConvs] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  const loadConvs = useCallback(() => {
    api.get("/conversations").then(({ data }) => setConvs(data)).catch(() => {});
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    if (!activeId) { setThread(null); return; }
    api.get(`/conversations/${activeId}/messages`).then(({ data }) => setThread(data)).catch(() => {});
  }, [activeId]);

  useEffect(() => {
    if (!user) return;
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/v1/ws";
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "message") {
            loadConvs();
            setThread((prev) => {
              if (!prev || prev.conversation.id !== payload.conversation_id) return prev;
              if (prev.messages.some((m) => m.id === payload.message.id)) return prev;
              return { ...prev, messages: [...prev.messages, payload.message] };
            });
          }
        } catch {}
      };
    } catch {}
    return () => { try { ws?.close(); } catch {} };
  }, [user, loadConvs]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const body = text;
    setText("");
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { body });
      setThread((prev) => prev && prev.messages.some((m) => m.id === data.id) ? prev : { ...prev, messages: [...prev.messages, data] });
    } catch (err) {
      setText(body);
      toast.error(errText(err));
    }
  };

  const reportUser = async () => {
    const other = thread?.conversation?.other_user;
    if (!other || !window.confirm(`${other.username} wirklich melden?`)) return;
    try {
      const { data } = await api.post("/reports", { entity_type: "user", entity_id: other.id, reason: "UNANGEMESSEN", details: `Gemeldet aus Chat (Unterhaltung ${activeId})` });
      toast.success(`Meldung ${data.number} eingereicht`, { description: "Unser Team prüft den Fall." });
    } catch (e) { toast.error(errText(e)); }
  };

  const blockUser = async () => {
    const other = thread?.conversation?.other_user;
    if (!other || !window.confirm(`${other.username} blockieren? Der Chat wird ausgeblendet und ihr könnt euch nicht mehr schreiben.`)) return;
    try {
      const { data } = await api.post(`/users/${other.id}/block`);
      toast.success(`${other.username} blockiert`, { description: data.number ? `Meldung ${data.number} wurde automatisch erstellt.` : undefined });
      setParams({});
      loadConvs();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl sm:text-4xl font-bold mb-6">Nachrichten</h1>
      <div className="grid md:grid-cols-3 gap-4 md:h-[65vh]">
        <div className={`border border-border rounded-2xl overflow-y-auto ${activeId ? "hidden md:block" : ""}`} data-testid="conversation-list">
          {convs.length === 0 && <p className="p-6 text-sm text-muted-foreground">Noch keine Unterhaltungen.</p>}
          {convs.map((c) => (
            <button key={c.id} data-testid={`conversation-${c.id}`} onClick={() => setParams({ c: c.id })}
              className={`w-full flex items-center gap-3 p-3.5 text-left hover:bg-secondary/50 transition-colors border-b border-border ${activeId === c.id ? "bg-secondary/60" : ""}`}>
              {c.item?.image_ids?.[0] ? (
                <img src={imgUrl(c.item.image_ids[0], "thumb")} alt="" className="w-12 h-12 rounded-xl object-cover" />
              ) : <div className="w-12 h-12 rounded-xl bg-muted" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{c.other_user?.username}</p>
                <p className="text-xs text-muted-foreground truncate">{c.item?.title}</p>
                <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>
              </div>
              {c.unread > 0 && <span className="bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center">{c.unread}</span>}
            </button>
          ))}
        </div>

        <div className={`md:col-span-2 border border-border rounded-2xl flex flex-col ${!activeId ? "hidden md:flex" : ""}`}>
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Wähle eine Unterhaltung</div>
          ) : (
            <>
              <div className="flex items-center gap-1 border-b border-border" data-testid="thread-item-header">
                <Link to={`/artikel/${thread.conversation.item?.id}`} className="flex items-center gap-3 p-3.5 flex-1 min-w-0 hover:bg-secondary/40">
                  {thread.conversation.item?.image_ids?.[0] && <img src={imgUrl(thread.conversation.item.image_ids[0], "thumb")} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{thread.conversation.item?.title}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">{thread.conversation.item ? formatPrice(thread.conversation.item.price) : ""} · mit {thread.conversation.other_user?.username}
                      {thread.conversation.other_user?.badges?.[0] && <BadgeChip badge={thread.conversation.other_user.badges[0]} />}
                    </p>
                  </div>
                </Link>
                <button data-testid="chat-report-user-btn" onClick={reportUser} title="Nutzer melden"
                  className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                  <Flag className="w-4 h-4" />
                </button>
                <button data-testid="chat-block-user-btn" onClick={blockUser} title="Nutzer blockieren"
                  className="p-2 mr-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                  <Ban className="w-4 h-4" />
                </button>
              </div>
              {thread.offers?.length > 0 && (
                <div className="p-3 space-y-2 border-b border-border max-h-56 overflow-y-auto" data-testid="thread-offers">
                  {thread.offers.slice(0, 3).map((o) => (
                    <OfferCard key={o.id} offer={o} user={user}
                      onAction={() => api.get(`/conversations/${activeId}/messages`).then(({ data }) => setThread(data))} />
                  ))}
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4 space-y-2.5 min-h-[300px]" data-testid="message-thread">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${m.sender_id === user.id ? "bg-primary text-primary-foreground rounded-br-md" : "bg-secondary rounded-bl-md"}`}>
                      {m.body}
                      <p className={`text-[10px] mt-1 ${m.sender_id === user.id ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                        {new Date(m.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                        {m.sender_id === user.id && (m.read ? " · Gelesen" : "")}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Nachricht schreiben…"
                  className="flex-1 rounded-full border border-border bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                <button data-testid="message-submit-btn" disabled={!text.trim()} className="bg-primary text-primary-foreground rounded-full p-3 hover:opacity-90 active:scale-95 transition-transform disabled:opacity-50">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
